import React, { useCallback, useEffect, useState } from "react";
import { YearAndPeriod } from "@/models/";
import { Apartment, ApartmentStats, PayoutStat } from "@/models/responses";
import { useAppData } from "@/hooks/useAppData";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../shadcn/ui/table";
import { useAllApartments } from "@/store/slices/apartments";
import { fetchOptions, getApiBasePath } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import { toast } from "@/lib/shadcn/hooks/use-toast";
import { computeStat, computeTax } from "./reportSummary.tax";

interface ReportDistributionStatsProps {
    apartments: Apartment[];
    apartmentStats: ApartmentStats[];
    yearAndPeriod: YearAndPeriod;
}

const ReportDistributionStats: React.FC<ReportDistributionStatsProps> = ({ apartments, yearAndPeriod, apartmentStats }) => {
    const { selectedOwner } = useAppData();
    const apartmentDetails = useAllApartments();
    const [reportApartmentStats, setReportApartmentStats] = useState<ApartmentStats[]>([]);
    const [payoutStats, setPayoutStats] = useState<PayoutStat[]>([]);
    const [payoutStatsLastYear, setPayoutStatsLastYear] = useState<PayoutStat[]>([]);

    const computeTotalPayoutStats = useCallback(
        (stats: PayoutStat[]): PayoutStat => {
            let totalStats: PayoutStat = {
                fromDate: new Date(),
                toDate: new Date(),
                apartmentId: 0,
                expenses: 0,
                managementFee: 0,
                grossIncome: 0,
                payout: 0,
                payoutRatio: 0,
                deposit: 0,
                cumulativeDeposit: 0,
                netIncome: 0,
            };
            stats.forEach((stat) => {
                let isFiftyPc = apartmentDetails.find((apartment) => apartment.id === stat.apartmentId)?.ownerIds.length === 2;
                totalStats.payout += computeStat(stat.payout, isFiftyPc);
                totalStats.grossIncome += computeStat(stat.grossIncome, isFiftyPc);
                totalStats.deposit += computeStat(stat.deposit, isFiftyPc);
                totalStats.cumulativeDeposit += computeStat(stat.cumulativeDeposit, isFiftyPc);
                totalStats.netIncome += computeStat(stat.netIncome, isFiftyPc);
            });
            return totalStats;
        },
        [apartmentDetails],
    );

    const [currentTotalPayoutStats, setCurrentTotalPayoutStats] = useState<PayoutStat>({
        fromDate: new Date(),
        toDate: new Date(),
        apartmentId: 0,
        cumulativeDeposit: 0,
        expenses: 0,
        managementFee: 0,
        grossIncome: 0,
        netIncome: 0,
        payout: 0,
        payoutRatio: 0,
        deposit: 0,
    });

    useEffect(() => {
        setReportApartmentStats(apartmentStats);
    }, [apartmentStats]);

    useEffect(() => {
        if (apartments.length === 0) {
            return;
        }
        let url = `${apiUrl}${getApiBasePath()}/owners/${selectedOwner}/payouts?year=${yearAndPeriod.year}&period=${yearAndPeriod.period}`;
        fetch(url, fetchOptions)
            .then((response) => {
                if (response) {
                    return response.text();
                }
                throw new Error("Unauthorized");
            })
            .then((data) => {
                const payoutStats = JSON.parse(data).payoutStats;
                setPayoutStats(payoutStats);
                setCurrentTotalPayoutStats(computeTotalPayoutStats(payoutStats));
            })
            .catch((error) =>
                toast({
                    title: "Error",
                    description: `Failed to fetch payout stats: ${error.message}`,
                    variant: "destructive",
                }),
            );
        let urlLastYear = `${apiUrl}${getApiBasePath()}/owners/${selectedOwner}/payouts?year=${yearAndPeriod.year - 1}&period=${yearAndPeriod.period}`;
        fetch(urlLastYear, fetchOptions)
            .then((response) => {
                if (response) {
                    return response.text();
                }
                throw new Error("Unauthorized");
            })
            .then((data) => {
                const payoutStats = JSON.parse(data).payoutStats;
                setPayoutStatsLastYear(payoutStats);
            })
            .catch((error) => {
                toast({
                    title: "Error",
                    description: `Failed to fetch payout stats: ${error.message}`,
                    variant: "destructive",
                });
            });
    }, [yearAndPeriod, apartments, apartmentDetails, selectedOwner, computeTotalPayoutStats]);

    function getApartmentStats(apartmentId: number, isFiftyPc: boolean) {
        if (!reportApartmentStats) {
            return null;
        }
        const stats = reportApartmentStats.find((s) => s.apartmentId === apartmentId);
        const multiplier = isFiftyPc ? 0.5 : 1;
        if (!stats) {
            return null;
        }
        return {
            grossIncome: Math.round(stats.grossIncome * multiplier),
            vat: Math.round(stats.vat * multiplier),
            sustainabilityFee: Math.round(stats.sustainabilityFee * multiplier),
            temporaryStayDuty: Math.round(stats.temporaryStayDuty * multiplier),
            businessFee: Math.round(stats.businessFee * multiplier),
            platformCommissions: Math.round(stats.platformCommissions * multiplier),
            cleaningFees: Math.round(stats.cleaningFees * multiplier),
            netIncome: Math.round(stats.netIncome * multiplier),
        };
    }

    function getPayoutStats(apartmentId: number, isFiftyPc: boolean) {
        if (!payoutStats) {
            return null;
        }
        const stats = payoutStats.find((s) => s.apartmentId === apartmentId);
        const multiplier = isFiftyPc ? 0.5 : 1;
        if (!stats) {
            return null;
        }
        return {
            payout: Math.round(stats.payout * multiplier),
            grossIncome: Math.round(stats.grossIncome * multiplier),
            deposit: Math.round(stats.deposit * multiplier),
            cumulativeDeposit: Math.round(stats.cumulativeDeposit * multiplier),
            netIncome: Math.round(stats.netIncome * multiplier),
        };
    }

    function getLastYearPayoutStats(apartmentId: number, isFiftyPc: boolean) {
        if (!payoutStatsLastYear) {
            return null;
        }
        const stats = payoutStatsLastYear.find((s) => s.apartmentId === apartmentId);
        const multiplier = isFiftyPc ? 0.5 : 1;
        if (!stats) {
            return null;
        }
        return {
            payout: Math.round(stats.payout * multiplier),
            periodDeposit: Math.round(stats.deposit * multiplier),
            cumulativeDeposit: Math.round(stats.cumulativeDeposit * multiplier),
            netIncome: Math.round(stats.netIncome * multiplier),
        };
    }

    function computeTotalStats() {
        let totalStats = {
            grossIncome: 0,
            vat: 0,
            businessFee: 0,
            sustainabilityFee: 0,
            temporaryStayDuty: 0,
            platformCommissions: 0,
            cleaningFees: 0,
            netIncome: 0,
        };
        apartmentStats.forEach((stat) => {
            let isFiftyPc = apartmentDetails.find((apartment) => apartment.id === stat.apartmentId)?.ownerIds.length === 2;
            totalStats.grossIncome += computeStat(stat.grossIncome, isFiftyPc);
            totalStats.vat += computeStat(stat.vat, isFiftyPc);
            totalStats.businessFee += computeStat(stat.businessFee, isFiftyPc);
            totalStats.sustainabilityFee += computeStat(stat.sustainabilityFee, isFiftyPc);
            totalStats.temporaryStayDuty += computeStat(stat.temporaryStayDuty, isFiftyPc);
            totalStats.platformCommissions += computeStat(stat.platformCommissions, isFiftyPc);
            totalStats.cleaningFees += computeStat(stat.cleaningFees, isFiftyPc);
            totalStats.netIncome += computeStat(stat.netIncome, isFiftyPc);
        });
        return totalStats;
    }

    return (
        <div className="space-y-8">
            <div className="w-full report-table-wrapper">
                <Table className="text-[11px] report-table">
                    <TableHeader className="bg-muted">
                        <TableRow className="hover:bg-muted">
                            <TableHead className="py-2 px-1 text-left font-medium">Apartment</TableHead>
                            <TableHead className="py-2 px-1 text-right font-medium whitespace-nowrap">
                                Gross
                                <br />
                                Income
                            </TableHead>
                            <TableHead className="py-2 px-1 text-right font-medium">VAT 13%</TableHead>
                            {yearAndPeriod.year >= 2026 && <TableHead className="py-2 px-1 text-right font-medium">Business Fee</TableHead>}
                            <TableHead className="py-2 px-1 text-right font-medium whitespace-nowrap">
                                Sustain.
                                <br />
                                Fee
                            </TableHead>
                            <TableHead className="py-2 px-1 text-right font-medium whitespace-nowrap">
                                Temporary
                                <br />
                                Stay Duty
                            </TableHead>
                            <TableHead className="py-2 px-1 text-right font-medium whitespace-nowrap">
                                Platform
                                <br />
                                Comms
                            </TableHead>
                            <TableHead className="py-2 px-1 text-right font-medium whitespace-nowrap">
                                Cleaning
                                <br />
                                Fees
                            </TableHead>
                            <TableHead className="py-2 px-1 text-right font-medium">Net Income</TableHead>
                            <TableHead className="py-2 px-1 text-right font-medium bg-muted whitespace-nowrap">
                                Dist. <br />
                                {yearAndPeriod.period} {yearAndPeriod.year}
                            </TableHead>
                            <TableHead className="py-2 px-1 text-right font-medium whitespace-nowrap">
                                Dist. <br />
                                {yearAndPeriod.period} {yearAndPeriod.year - 1}
                            </TableHead>
                            <TableHead className="py-2 px-1 text-right font-medium bg-muted whitespace-nowrap">
                                Dist. <br /> {yearAndPeriod.year}
                            </TableHead>
                            <TableHead className="py-2 px-1 text-right font-medium whitespace-nowrap">
                                Dist. Amount <br />
                                {yearAndPeriod.year - 1}
                            </TableHead>
                            <TableHead className="py-2 px-1 text-right font-medium whitespace-nowrap">
                                {yearAndPeriod.period} Cash
                                <br />
                                Deposit
                            </TableHead>
                            <TableHead className="py-2 px-1 text-right font-medium whitespace-nowrap">
                                Cum. Cash
                                <br />
                                Deposit
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {apartments.map((apartment) => {
                            const isFiftyPc = apartment.ownerIds.length === 2;
                            const stats = getApartmentStats(apartment.id, isFiftyPc);
                            const currentPayoutStats = getPayoutStats(apartment.id, isFiftyPc);
                            const lastYearPayoutStats = getLastYearPayoutStats(apartment.id, isFiftyPc);
                            const apartmentName = isFiftyPc ? `${apartment.shortName || ""} (50%)` : apartment.shortName;

                            return (
                                <TableRow key={apartment.id} className="hover:bg-muted">
                                    <TableCell className="py-2 px-1 font-medium">{apartmentName}</TableCell>
                                    <TableCell className="py-2 px-1 text-right font-medium whitespace-nowrap">{currentPayoutStats?.grossIncome} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{stats?.vat} €</TableCell>
                                    {yearAndPeriod.year >= 2026 && (
                                        <TableCell className="py-2 px-1 text-right whitespace-nowrap">{stats?.businessFee} €</TableCell>
                                    )}
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{stats?.sustainabilityFee} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{stats?.temporaryStayDuty} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{stats?.platformCommissions} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{stats?.cleaningFees} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{stats?.netIncome} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right bg-muted whitespace-nowrap">
                                        {(((currentPayoutStats?.payout || 0) / (currentPayoutStats?.netIncome || 1)) * 100).toFixed(0)}%
                                    </TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">
                                        {(((lastYearPayoutStats?.payout || 0) / (lastYearPayoutStats?.netIncome || 1)) * 100).toFixed(0)}%
                                    </TableCell>
                                    <TableCell className="py-2 px-1 text-right bg-muted whitespace-nowrap">{currentPayoutStats?.payout} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{lastYearPayoutStats?.payout} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{currentPayoutStats?.deposit} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{currentPayoutStats?.cumulativeDeposit} €</TableCell>
                                </TableRow>
                            );
                        })}
                        {/* Total Row */}
                        {(() => {
                            let totalStats = computeTotalStats();
                            let lastYearPayoutStats = computeTotalPayoutStats(payoutStatsLastYear);
                            return (
                                <TableRow className="hover:bg-muted font-medium">
                                    <TableCell className="py-2 px-1">Total</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{totalStats.grossIncome} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{totalStats.vat} €</TableCell>
                                    {yearAndPeriod.year >= 2026 && (
                                        <TableCell className="py-2 px-1 text-right whitespace-nowrap">{totalStats.businessFee} €</TableCell>
                                    )}
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{totalStats.sustainabilityFee} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{totalStats.temporaryStayDuty} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{totalStats.platformCommissions} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{totalStats.cleaningFees} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{totalStats.netIncome} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right bg-muted whitespace-nowrap">
                                        {(((currentTotalPayoutStats?.payout || 0) / (currentTotalPayoutStats?.netIncome || 1)) * 100).toFixed(0)}%
                                    </TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">
                                        {(((lastYearPayoutStats?.payout || 0) / (lastYearPayoutStats?.netIncome || 1)) * 100).toFixed(0)}%
                                    </TableCell>
                                    <TableCell className="py-2 px-1 text-right bg-muted whitespace-nowrap">{currentTotalPayoutStats?.payout} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{lastYearPayoutStats?.payout} €</TableCell>
                                    <TableCell className="py-2 px-1 text-right whitespace-nowrap">{currentTotalPayoutStats?.deposit} €</TableCell>
                                    <TableCell
                                        className={`py-2 px-1 text-right whitespace-nowrap ${
                                            currentTotalPayoutStats?.cumulativeDeposit > 0 ? "bg-muted" : "bg-red-50"
                                        }`}
                                    >
                                        {currentTotalPayoutStats?.cumulativeDeposit} €
                                    </TableCell>
                                </TableRow>
                            );
                        })()}
                    </TableBody>
                </Table>
            </div>

            {/* Tax Benefit Table */}
            <div className="space-y-2">
                <h5 className="text-lg font-semibold">Tax Benefit</h5>
                <div className="w-full max-w-2xl">
                    <Table className="text-[11px]">
                        <TableBody>
                            <TableRow>
                                <TableCell className="py-2 px-1 font-medium">Taxable Income for {yearAndPeriod.year}</TableCell>
                                <TableCell className="py-2 px-1 text-right whitespace-nowrap">{currentTotalPayoutStats.payout} €</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="py-2 px-1 font-medium">Projected Tax</TableCell>
                                <TableCell className="py-2 px-1 text-right whitespace-nowrap">{computeTax(currentTotalPayoutStats.payout)} €</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="py-2 px-1 font-medium">Tax on Gross Income of {currentTotalPayoutStats.grossIncome} €</TableCell>
                                <TableCell className="py-2 px-1 text-right whitespace-nowrap">{computeTax(currentTotalPayoutStats.grossIncome)} €</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="py-2 px-1 font-medium">Tax Benefit compared to Non-Declaring Expenses</TableCell>
                                <TableCell className="py-2 px-1 text-right whitespace-nowrap">
                                    {computeTax(currentTotalPayoutStats.grossIncome) - computeTax(currentTotalPayoutStats.payout)} €
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

export default ReportDistributionStats;
